// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/customerLifecycle/OrganizationBootstrap.js

import React, { useCallback, useEffect, useState } from "react";

import oasysLogo from "../../assets/oasys-logo.png";
import { bootstrapCustomerOrganization } from "../../services/customerLifecycleApi";

import "./CustomerLifecycle.css";

function decodeJwtPayload(token) {
  const parts = String(token || "").split(".");
  if (parts.length < 2) return null;

  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

async function readVerifiedAuth0Profile({ getAccessToken, signal }) {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("OASYS could not obtain your authenticated identity token.");
  }

  const claims = decodeJwtPayload(token);
  const issuer = String(claims?.iss || "").trim().replace(/\/+$/, "");
  const subject = String(claims?.sub || "").trim();

  if (!issuer || !subject) {
    throw new Error("OASYS could not validate your authenticated identity profile.");
  }

  const response = await fetch(`${issuer}/userinfo`, {
    method: "GET",
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const text = await response.text();
  let profile = {};

  if (text) {
    try {
      profile = JSON.parse(text);
    } catch {
      profile = {};
    }
  }

  if (!response.ok) {
    throw new Error(`OASYS could not confirm your verified email (${response.status}).`);
  }

  if (String(profile?.sub || "").trim() !== subject) {
    throw new Error("OASYS could not validate your authenticated identity profile.");
  }

  const email = String(profile?.email || "").trim();
  const givenName = String(profile?.given_name || "").trim();
  const familyName = String(profile?.family_name || "").trim();
  const combinedName = `${givenName} ${familyName}`.trim();
  const auth0Name = String(profile?.name || "").trim();
  const candidateName = combinedName || auth0Name;
  const fullName =
    candidateName && candidateName.toLowerCase() !== email.toLowerCase()
      ? candidateName
      : "";

  return {
    email,
    emailVerified: profile?.email_verified === true,
    fullName,
  };
}

export default function OrganizationBootstrap({
  apiBaseUrl,
  getAccessToken,
  onBootstrapComplete,
  onLogout,
}) {
  const [fullName, setFullName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [verificationState, setVerificationState] = useState("checking");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [verificationNonce, setVerificationNonce] = useState(0);

  const checkVerification = useCallback(async (signal, { silent = false } = {}) => {
    if (!silent) {
      setVerificationState("checking");
    }
    setVerificationError("");

    try {
      const profile = await readVerifiedAuth0Profile({ getAccessToken, signal });
      setVerificationEmail(profile.email);
      setFullName((current) => current.trim() || profile.fullName);
      setVerificationState(profile.emailVerified ? "verified" : "unverified");
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Customer email verification check failed:", err);
      setVerificationError(
        err?.message || "OASYS could not confirm your email verification status."
      );
      setVerificationState("error");
    }
  }, [getAccessToken]);

  useEffect(() => {
    const controller = new AbortController();
    checkVerification(controller.signal);
    return () => controller.abort();
  }, [checkVerification, verificationNonce]);

  useEffect(() => {
    if (verificationState !== "unverified") return undefined;

    const controller = new AbortController();
    let checking = false;

    const refreshVerification = async () => {
      if (checking || controller.signal.aborted) return;
      checking = true;
      try {
        await checkVerification(controller.signal, { silent: true });
      } finally {
        checking = false;
      }
    };

    const intervalId = window.setInterval(refreshVerification, 2000);
    window.addEventListener("focus", refreshVerification);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshVerification);
      controller.abort();
    };
  }, [checkVerification, verificationState]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedFullName = fullName.trim();
    const normalizedName = organizationName.trim();

    if (!normalizedFullName) {
      setError("Enter your full name to continue.");
      return;
    }

    if (!normalizedName) {
      setError("Enter your organization name to continue.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await bootstrapCustomerOrganization({
        apiBaseUrl,
        getAccessToken,
        fullName: normalizedFullName,
        organizationName: normalizedName,
        region: "us",
        dataResidencyPolicy: "US_ONLY",
      });

      if (result?.entry_state !== "existing_member") {
        throw new Error(
          "OASYS created the organization but could not resolve the new workspace. Please retry."
        );
      }

      onBootstrapComplete?.(result);
    } catch (err) {
      console.error("Customer organization bootstrap failed:", err);
      setError(err?.message || "OASYS could not create your organization.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verificationState === "checking") {
    return (
      <div className="customer-lifecycle-page">
        <section className="customer-lifecycle-card" aria-live="polite">
          <div className="customer-lifecycle-brand">
            <img src={oasysLogo} alt="OASYS" className="customer-lifecycle-logo" />
          </div>
          <div className="customer-lifecycle-heading">
            <p className="customer-lifecycle-eyebrow">Secure workspace setup</p>
            <h1>Confirming your email</h1>
            <p>OASYS is confirming that your authenticated email is verified.</p>
          </div>
          <div className="customer-lifecycle-progress" aria-label="Confirming verified email" />
        </section>
      </div>
    );
  }

  if (verificationState === "unverified") {
    return (
      <div className="customer-lifecycle-page">
        <section className="customer-lifecycle-card" aria-labelledby="oasys-verify-title">
          <div className="customer-lifecycle-brand">
            <img src={oasysLogo} alt="OASYS" className="customer-lifecycle-logo" />
          </div>

          <div className="customer-lifecycle-heading">
            <p className="customer-lifecycle-eyebrow">Email verification</p>
            <h1 id="oasys-verify-title">Verify your email</h1>
            <p>
              Verify the email address on your OASYS account before creating your organization
              workspace.
            </p>
          </div>

          <div className="customer-lifecycle-verification-panel" role="status">
            <strong>Check your inbox</strong>
            <p>
              OASYS sent a verification link
              {verificationEmail ? <> to <b>{verificationEmail}</b></> : " to your account email"}.
              Open that link. This page will continue automatically after verification.
            </p>
            <span>If you don't see the message, check your spam folder.</span>
          </div>

          <div
            className="customer-lifecycle-progress"
            aria-label="Waiting for email verification"
          />

          <div className="customer-lifecycle-actions customer-lifecycle-actions--stacked">
            {typeof onLogout === "function" ? (
              <button
                type="button"
                className="customer-lifecycle-secondary-button"
                onClick={onLogout}
              >
                Use a different account
              </button>
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  if (verificationState === "error") {
    return (
      <div className="customer-lifecycle-page">
        <section className="customer-lifecycle-card" aria-labelledby="oasys-verify-error-title">
          <div className="customer-lifecycle-brand">
            <img src={oasysLogo} alt="OASYS" className="customer-lifecycle-logo" />
          </div>

          <div className="customer-lifecycle-heading">
            <p className="customer-lifecycle-eyebrow">Email verification</p>
            <h1 id="oasys-verify-error-title">We couldn't confirm your email</h1>
            <p>OASYS must confirm a verified email before creating an organization workspace.</p>
          </div>

          <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
            {verificationError}
          </div>

          <div className="customer-lifecycle-actions">
            <button
              type="button"
              className="customer-lifecycle-primary-button"
              onClick={() => setVerificationNonce((value) => value + 1)}
            >
              Retry
            </button>
            {typeof onLogout === "function" ? (
              <button
                type="button"
                className="customer-lifecycle-secondary-button"
                onClick={onLogout}
              >
                Use a different account
              </button>
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="customer-lifecycle-page">
      <section className="customer-lifecycle-card" aria-labelledby="oasys-bootstrap-title">
        <div className="customer-lifecycle-brand">
          <img src={oasysLogo} alt="OASYS" className="customer-lifecycle-logo" />
        </div>

        <div className="customer-lifecycle-heading">
          <p className="customer-lifecycle-eyebrow">Secure workspace setup</p>
          <h1 id="oasys-bootstrap-title">Welcome to OASYS</h1>
          <p>
            Create your organization workspace. OASYS will securely establish your
            owner access and take you directly into Mission Launch.
          </p>
        </div>

        <form className="customer-lifecycle-form" onSubmit={handleSubmit}>
          <label className="customer-lifecycle-field" htmlFor="oasys-full-name">
            <span>Full name</span>
            <input
              id="oasys-full-name"
              name="fullName"
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              autoComplete="name"
              maxLength={200}
              disabled={submitting}
              autoFocus={!fullName.trim()}
              placeholder="Your full name"
            />
          </label>

          <label className="customer-lifecycle-field" htmlFor="oasys-organization-name">
            <span>Organization name</span>
            <input
              id="oasys-organization-name"
              name="organizationName"
              type="text"
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              autoComplete="organization"
              maxLength={200}
              disabled={submitting}
              autoFocus={Boolean(fullName.trim())}
              placeholder="Your company or organization"
            />
          </label>

          {error ? (
            <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            className="customer-lifecycle-primary-button"
            disabled={submitting || !fullName.trim() || !organizationName.trim()}
          >
            {submitting ? "Creating workspace…" : "Continue"}
          </button>
        </form>

        <div className="customer-lifecycle-footnote">
          <span>Your verified account is authenticated by OASYS identity services.</span>
          {typeof onLogout === "function" ? (
            <button type="button" className="customer-lifecycle-link-button" onClick={onLogout}>
              Use a different account
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
